import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { fetchProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const VARIANT_IDS = ["ivory"];

const Ivory = () => {
  const [variants, setVariants] = useState([]);
  const [selectedId, setSelectedId] = useState("ivory");
  const [qty, setQty] = useState(1);
  const [error, setError] = useState(null);
  const { addItem, openCart, MAX_PER_PURCHASE } = useCart();

  useEffect(() => {
    Promise.all(VARIANT_IDS.map((id) => fetchProduct(id).catch(() => null)))
      .then((results) => {
        const loaded = results.filter(Boolean);
        setVariants(loaded);
      })
      .catch((e) => setError(e.message));
  }, []);

  const selected = useMemo(
    () => variants.find((v) => v.id === selectedId) || variants[0],
    [variants, selectedId],
  );

  useEffect(() => {
    // Reset qty when switching sizes
    setQty(1);
  }, [selectedId]);

  const handleAdd = () => {
    if (!selected) return;
    addItem(selected.id, qty);
    toast.success(`Added ${qty} × No. 1 — Ivory (${selected.size}) to your cart`);
    openCart();
  };

  if (error) {
    return (
      <div className="pt-32 px-6 text-center text-red-400">
        Could not load product: {error}
      </div>
    );
  }

  if (!selected) {
    return (
      <div className="pt-32 px-6 text-center text-[var(--secondary-text)] uppercase tracking-label text-[11px]">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="ivory-page" className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        {/* Sticky image */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="relative"
        >
          <div className="lg:sticky lg:top-28">
            <div className="relative bg-black aspect-square flex items-center justify-center border hairline grain-overlay">
              <img
                src={selected.image}
                alt={selected.name}
                className="max-h-[88%] w-auto object-contain"
                data-testid="ivory-hero-image"
              />
            </div>
            <div className="flex items-center gap-4 mt-6">
              <img src="/mountain-mark.png" alt="" aria-hidden className="h-4 w-auto select-none" draggable={false} />
              <span className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
                Hand-crafted and Hand-bottled for your enjoyment. Edition 2026
              </span>
            </div>
          </div>
        </motion.div>

        {/* Details */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.2 }}
          className="lg:pt-12"
        >
          <span className="text-[10px] uppercase tracking-label gold-text">
            Our First Composition
          </span>
          <h1 className="mt-5 font-serif text-5xl md:text-6xl leading-[1.0] tracking-tight">
            No. 1 - Ivory
          </h1>
          <p className="mt-4 text-[11px] uppercase tracking-label text-[var(--secondary-text)]">
            {selected.tagline} · {selected.size}
          </p>

          <div className="divider-gold mt-8 mb-8" />

          <p className="text-base text-[var(--secondary-text)] leading-loose max-w-lg">
            Our first scent, No. 1 - Ivory, is a rich blend of 14 ingredients. Our four richest ingredients — Cedarwood, Sandalwood, Vanilla, and Amber — give a gentle balance of both warmth and spice, the perfect combination for all men. Along side these main four, you have other ingredients such as Clary sage, Lemon, and Bergamot - ingredients that give off hints of citrus and herb. This scent is ideal for any time, any place, and any season.
          </p>

          {/* Size selector */}
          {variants.length > 1 && (
            <div className="mt-12 border-t hairline pt-8">
              <p className="text-[10px] uppercase tracking-label gold-text mb-4">
                Choose size
              </p>
              <div className="grid grid-cols-2 gap-3" data-testid="ivory-size-selector">
                {variants.map((v) => {
                  const active = v.id === selectedId;
                  return (
                    <button
                      key={v.id}
                      onClick={() => setSelectedId(v.id)}
                      data-testid={`size-option-${v.id}`}
                      className={`text-left border hairline px-5 py-4 transition-colors ${
                        active
                          ? "border-[var(--accent-gold)] bg-[#0a0a0a]"
                          : "hover:border-[var(--accent-gold)]/60"
                      }`}
                    >
                      <div className={`text-sm ${active ? "text-[var(--accent-gold)]" : "text-[var(--primary-text)]"}`}>
                        {v.size}
                      </div>
                      <div className="mt-1 text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
                        Eau de Parfum
                      </div>
                      <div className="mt-2 font-serif text-lg text-[var(--primary-text)]">
                        ${v.price?.toFixed(2)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Price + qty + add */}
          <div className="mt-10">
            <div className="flex items-baseline justify-between mb-10">
              <span
                className="font-serif text-4xl gold-text"
                data-testid="ivory-price"
              >
                ${selected.price?.toFixed(2)}
              </span>
              <span className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
                Limit {MAX_PER_PURCHASE} per order
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch gap-5">
              <div className="inline-flex items-center self-start sm:self-auto">
                <button
                  className="qty-btn"
                  data-testid="ivory-decrease-btn"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  disabled={qty <= 1}
                  aria-label="Decrease quantity"
                >
                  <Minus size={14} />
                </button>
                <span
                  className="w-14 text-center text-sm tracking-label"
                  data-testid="ivory-qty"
                >
                  {qty}
                </span>
                <button
                  className="qty-btn"
                  data-testid="ivory-increase-btn"
                  onClick={() => setQty((q) => Math.min(MAX_PER_PURCHASE, q + 1))}
                  disabled={qty >= MAX_PER_PURCHASE}
                  aria-label="Increase quantity"
                >
                  <Plus size={14} />
                </button>
              </div>

              <button
                onClick={handleAdd}
                data-testid="ivory-add-to-cart-btn"
                className="btn-secondary inline-flex items-center justify-center gap-3 flex-1"
              >
                <ShoppingBag size={14} />
                Add to Cart — ${(selected.price * qty).toFixed(2)}
              </button>
            </div>

            <ul className="mt-10 space-y-3 text-[11px] uppercase tracking-label text-[var(--secondary-text)]">
              <li>· 10+ hour long concentration</li>
              <li>· 14 premium ingredients</li>
              <li>· $6.99 flat shipping · ships within United States</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Ivory;
